import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Categories,
  IMomentTemplate,
  MomentTemplateStatus
} from '@dadohr/shared/domainTypes/experience';
import { FS } from '@dadohr/shared/versions';
import shortid from 'shortid';
import { UnsafeTFunction } from '../../../i18next';
import { setDoc, store } from '../../../services/firebase';
import {
  CollectionListener,
  createCollectionListenerStore,
  useCollectionListener
} from '../../../services/firecache/collectionListener';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../../../services/firecache/documentListener';
import { fromMoment, now, toMoment } from '../../../services/time';
import { toFullName } from '../../../services/user';
import { toMomentTemplateDoc } from './documents';

export const CategoriesLabels: {
  [key in Categories]: (t: UnsafeTFunction) => string;
} = {
  [Categories.COMPANY_ANNOUNCEMENTS]: (t) => t('Company announcements'),
  [Categories.EMPLOYEE_MILESTONE]: (t) => t('Employee milestone'),
  [Categories.US_HOLIDAYS]: (t) => t('US holidays'),
  [Categories.UK_HOLIDAYS]: (t) => t('UK holidays'),
  [Categories.CA_HOLIDAYS]: (t) => t('CA holidays'),
  [Categories.DE_HOLIDAYS]: (t) => t('DE holidays'),
  [Categories.AUS_HOLIDAYS]: (t) => t('AUS holidays'),
  [Categories.RELIGIOUS_HOLIDAYS]: (t) => t('Religious holidays'),
  [Categories.NEWSLETTERS]: (t) => t('Newsletters')
};

const templateTemplateCollection = () =>
  store().collection(FS.momentTemplateTemplates);

const getMomentTemplateTemplateListenerStore = createCollectionListenerStore(
  () =>
    new CollectionListener(templateTemplateCollection(), toMomentTemplateDoc)
);

export const useMomentTemplateTemplates = () => {
  return useCollectionListener(getMomentTemplateTemplateListenerStore(''));
};

const getMomentTemplateListenerById = createDocumentListenerGetter(
  (id) => templateTemplateCollection().doc(id),
  toMomentTemplateDoc
);
export const useMomentTemplatesTemplateById = (tplId: string) => {
  return useDocumentListener(getMomentTemplateListenerById(tplId));
};

export const convertMomentTemplateTemplateToDraft = (
  d: Doc<IMomentTemplate>,
  currentUser: ICurrentUser
): Doc<IMomentTemplate> => {
  const sendMoment = toMoment(d.data.sendDate.at).tz(currentUser.org.tz, true);

  return {
    id: shortid(),
    collection: FS.momentTemplates,

    data: {
      ...d.data,
      orgId: currentUser.org.id,
      tz: currentUser.org.tz,
      sendDate: {
        ...d.data.sendDate,
        at: fromMoment(sendMoment)
      },
      status: MomentTemplateStatus.DRAFT,
      createdAt: now(),
      createdBy: currentUser.id,
      updatedAt: now(),
      updatedBy: currentUser.id,
      replyTo: {
        email: currentUser.email
      },
      from: {
        name: toFullName(currentUser),
        email: currentUser.email
      },
      // TODO - all blocks should be properly copied and get new ids too
      // Which means that these new ids need to be featured in the audience blocks as well
      // This is also done when duplicating experience templates
      // The same TODO was added to duplicateMomentTemplate
      content: {
        ...d.data.content,
        audiences: d.data.content.audiences.map((a) => ({
          ...a,
          id: shortid()
        }))
      }
    }
  };
};

export const duplicateMomentTemplateTemplate = async (
  d: Doc<IMomentTemplate>,
  currentUser: ICurrentUser
) => {
  const nextD = convertMomentTemplateTemplateToDraft(d, currentUser);
  await setDoc(nextD);
  return nextD;
};
